/**
 * Lazy loader for pdfjs-dist (the PDF *reader*, consumed via react-pdf).
 *
 * pdfjs-dist's main-thread engine is ~409 KB (separate from its ~1 MB worker).
 * It is only needed when a PDF is actually viewed (PdfEditor) or has its text
 * extracted (documentHelper.extractPdfText). Importing it dynamically keeps it
 * out of the eager boot/store chunk — it is fetched on demand the first time a
 * PDF is read.
 *
 * Previously index.tsx imported `{pdfjs}` at the entry module purely to set
 * `GlobalWorkerOptions.workerSrc`; that static edge dragged the whole pdfjs
 * main-thread engine into the critical-path `store` chunk for every user,
 * including those who never open a PDF. Configuring the worker lazily here
 * removes that edge.
 */

// Type-only — fully erased at build time, creates NO runtime dependency on
// react-pdf/pdfjs-dist. `pdfjs` is the pdfjs-dist module namespace.
type Pdfjs = (typeof import('react-pdf'))['pdfjs'];

/**
 * Point pdfjs at its worker bundle. Idempotent: checks the actual target so it
 * is safe to call from multiple entry points (PdfEditor mount, text extraction)
 * and robust even if pdfjs were ever duplicated across chunks.
 */
export function configurePdfWorker(pdfjs: Pdfjs): void {
    // react-pdf v10 sets `GlobalWorkerOptions.workerSrc = 'pdf.worker.mjs'` at
    // import time — a bare specifier that resolves to `/assets/pdf.worker.mjs`,
    // which does not exist in the built app and 404s to index.html (the SPA
    // fallback), so pdfjs fails with "non-JavaScript MIME type text/html" and
    // then can't even spin up its fake worker. We must OVERRIDE that default
    // with the Vite-emitted, content-hashed worker URL — NOT skip when any
    // value is already present. (The pre-code-split index.tsx set this
    // unconditionally, which is why PDFs worked before the pdfjs code-split;
    // the old `if (workerSrc) return` guard let react-pdf's bogus default win.)
    // Guard only against our own resolved URL so the function stays idempotent.
    const workerSrc = new URL(
        'pdfjs-dist/build/pdf.worker.min.mjs',
        import.meta.url,
    ).toString();
    if (pdfjs.GlobalWorkerOptions.workerSrc === workerSrc) return;
    pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;
}

let pdfjsPromise: Promise<Pdfjs> | null = null;

/**
 * Dynamically load pdfjs (via react-pdf) and ensure its worker is configured.
 * The module is fetched once and the resolved instance is shared for the
 * session. Use this from non-React code paths (e.g. text extraction).
 */
export function loadPdfjs(): Promise<Pdfjs> {
    if (!pdfjsPromise) {
        pdfjsPromise = import('react-pdf').then(({pdfjs}) => {
            configurePdfWorker(pdfjs);
            return pdfjs;
        });
    }
    return pdfjsPromise;
}
